import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WaitlistForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    country: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!formData.role) {
      toast.error('Please select your role');
      return;
    }
    
    if (!formData.country) {
      toast.error('Please enter your country');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/waitlist`, formData);
      setIsSuccess(true);
      toast.success('Successfully joined the waitlist!');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ name: '', email: '', role: '', country: '', message: '' });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('This email is already on the waitlist');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      console.error('Waitlist submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <section id="waitlist" className="py-16 sm:py-24 lg:py-32 relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">Join Us</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-6 font-['Space_Grotesk']">
              Join early access
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground px-4">
              Be among the first to access Ref Assist and help shape the future of grassroots football.
            </p>
          </div>
          
          {/* Form Card */}
          <Card className="bg-card border-border/50 p-6 sm:p-8 lg:p-10 shadow-[0_0_60px_hsl(210_100%_56%_/_0.1)]">
            {isSuccess ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-2 font-['Space_Grotesk']">You're on the list!</h3>
                <p className="text-muted-foreground">We'll be in touch soon with updates.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-foreground font-medium">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-muted/50 border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground h-11"
                  />
                </div>
                
                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground font-medium">
                    Email <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-muted/50 border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground h-11"
                  />
                </div>
                
                {/* Role */}
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-foreground font-medium">
                    Role <span className="text-accent">*</span>
                  </Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })} required>
                    <SelectTrigger className="bg-muted/50 border-border/50 focus:border-primary text-foreground h-11">
                      <SelectValue placeholder="Select your role" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border">
                      <SelectItem value="referee">Referee</SelectItem>
                      <SelectItem value="club">Club</SelectItem>
                      <SelectItem value="league">League</SelectItem>
                      <SelectItem value="academy">Academy</SelectItem>
                      <SelectItem value="investor">Investor</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Country */}
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-foreground font-medium">
                    Country <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="country"
                    type="text"
                    placeholder="Your country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    required
                    className="bg-muted/50 border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground h-11"
                  />
                </div>
                
                {/* Message (Optional) */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-foreground font-medium">
                    Message <span className="text-muted-foreground text-sm">(optional)</span>
                  </Label>
                  <textarea
                    id="message"
                    placeholder="Tell us why you're interested or any specific needs..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows="3"
                    className="w-full bg-muted/50 border border-border/50 focus:border-primary text-foreground placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
                
                {/* Submit Button */}
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-12 text-base glow-primary"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    'Join Early Access'
                  )}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
};
